/* eslint-disable no-unused-vars */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Video,
  Headphones,
  Users,
  BookOpen,
  Heart,
  DollarSign,
  Sparkles,
  MessageCircle,
  Mic,
  ExternalLink,
  Brain,
  Dumbbell,
  Compass,
  Landmark,
  ArrowRight,
  Check,
  Flame,
  CalendarCheck,
  Quote,
} from 'lucide-react';
import PortalSubPage, { ComingSoonCard } from '../PortalSubPage';
import {
  NEXT_MEETING,
  INTEREST_MAILTO,
  INTEREST_EMAIL,
  CIRCLE_FOCUS,
  CIRCLE_GUEST_SPEAKER,
  CIRCLE_TOPICS,
  CIRCLE_PRICE,
  getReflectionForOffset,
} from '../../data/portalData';

export function PortalReflection() {
  const today = getReflectionForOffset(0);
  const upcoming = [1, 2, 3].map((d) => getReflectionForOffset(d));
  const yesterday = getReflectionForOffset(-1);

  return (
    <PortalSubPage
      eyebrow="Daily Reflection"
      title={
        <>
          One line. <span className="italic text-[#D4AF37]">Every day.</span>
        </>
      }
      intro={"A brother-to-brother thought to carry with you today. Sit with it. Act on it. Come back tomorrow for the next one."}
      icon={Quote}
    >
      {/* Today's card */}
      <div
        className="rounded-2xl bg-gradient-to-br from-[#D4AF37]/20 via-[#8C6239]/10 to-transparent border border-[#D4AF37]/30 p-8 md:p-12"
        data-testid="reflection-today"
      >
        <p className="text-[#D4AF37] font-manrope text-[11px] uppercase tracking-[0.3em] font-bold mb-4">
          Today &middot; {today.label}
        </p>
        <div className="relative">
          <Quote className="absolute -top-2 -left-2 w-8 h-8 text-[#D4AF37]/40" />
          <p
            className="font-cormorant text-3xl md:text-4xl text-white leading-snug italic pl-10 mb-5"
            data-testid="reflection-today-quote"
          >
            &ldquo;{today.quote}&rdquo;
          </p>
        </div>
        <p className="text-zinc-400 font-manrope text-sm uppercase tracking-widest pl-10">
          &mdash; {today.author}
        </p>
      </div>

      {/* Yesterday recap */}
      <div
        className="rounded-2xl bg-[#111111] border border-white/10 p-5 md:p-6"
        data-testid="reflection-yesterday"
      >
        <p className="text-zinc-500 font-manrope text-[11px] uppercase tracking-widest font-bold mb-2">
          Missed yesterday? Catch up.
        </p>
        <p className="font-cormorant text-lg md:text-xl text-zinc-200 italic mb-1">
          &ldquo;{yesterday.quote}&rdquo;
        </p>
        <p className="text-zinc-500 font-manrope text-xs">&mdash; {yesterday.author}</p>
      </div>

      {/* Upcoming */}
      <div data-testid="reflection-upcoming">
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="font-cormorant text-2xl md:text-3xl font-medium text-white">
            Coming up
          </h3>
          <p className="text-zinc-500 font-manrope text-xs uppercase tracking-widest">
            Next 3 days
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          {upcoming.map((r, i) => (
            <div
              key={i}
              className="rounded-xl bg-[#0A0A0A] border border-white/10 p-5"
            >
              <p className="text-[#D4AF37]/80 font-manrope text-[10px] uppercase tracking-widest font-bold mb-3">
                {r.label}
              </p>
              <p className="text-zinc-300 font-manrope text-sm leading-relaxed line-clamp-4">
                &ldquo;{r.quote}&rdquo;
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Closing CTA */}
      <div className="rounded-2xl bg-[#111111] border border-white/10 p-6 md:p-8 text-center">
        <p className="text-[#D4AF37] font-manrope text-[11px] uppercase tracking-[0.3em] font-bold mb-2">
          Take it further
        </p>
        <h3 className="font-cormorant text-2xl md:text-3xl font-medium text-white mb-3">
          {"Turn today\u2019s line into an "}<span className="italic text-[#D4AF37]">answered prompt.</span>
        </h3>
        <p className="text-zinc-400 font-manrope text-sm mb-5 max-w-lg mx-auto">
          Open the 5 Pillars tracker and log which pillar this line spoke to.
        </p>
        <Link
          to="/portal/pillars"
          className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A0A0A] px-6 py-2.5 rounded-full font-manrope text-sm font-semibold hover:bg-amber-200 transition-colors"
          data-testid="reflection-to-pillars"
        >
          Open the tracker
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </PortalSubPage>
  );
}
